/**
 * Paging through an Instagram post's pictures in its preview, one at a time: the arrows' names,
 * which pictures are kept in the frame, and where focus goes when an arrow is taken away. Kept apart
 * from the component so the rules can be tested without a browser.
 *
 * The paging itself is the PDF carousel's (pdfPages.ts), counted the same way from 1: no wrapping
 * round - the ends are the ends - and an arrow with nowhere to go is taken away.
 */
import { arrowShown, type PageArrow } from './pdfPages';

/**
 * The two arrows, in the order a keyboard reaches them: the PDF carousel's sides and steps, named
 * for what they move through.
 */
export const IMAGE_ARROWS: readonly PageArrow[] = [
  { side: 'left', by: -1, label: 'Previous image' },
  { side: 'right', by: 1, label: 'Next image' },
];

/**
 * Where focus goes after `pressed` has brought the reader to `slide`: nowhere while that arrow is
 * still there (so it can be pressed again), and to the other arrow once it has been taken away at
 * the end it led to - as in the PDF carousel (`arrowToFocus`, which works over that carousel's own
 * arrows).
 */
export function imageArrowToFocus(pressed: PageArrow, slide: number, count: number): PageArrow | null {
  if (arrowShown(pressed, slide, count)) return null;
  return IMAGE_ARROWS.find((arrow) => arrow !== pressed && arrowShown(arrow, slide, count)) ?? null;
}

/**
 * The pictures kept in the frame, each loading its own: every one the reader has been shown -
 * `furthest` is the furthest so far - and the one after it. A step forward then slides in a picture
 * that is most likely loaded already, and a step back finds the one it left (a picture that left the
 * frame would be downloaded all over again: its object URL goes with it). And a post of ten pictures
 * is two downloads as the preview opens, not ten.
 */
export function keptSlides(slide: number, furthest: number, count: number): number[] {
  const last = Math.min(count, Math.max(slide, furthest) + 1);
  return Array.from({ length: Math.max(0, last) }, (_unused, i) => i + 1);
}
